import { useEffect } from 'react';

import { STAGE_STATE_LABEL, videoPaths, type Character, type VoiceEngine } from '@ai-studio/shared';

import { rawUrl } from '../../lib/thumbnails.js';
import { addCharacter, characterImage, nextId, pieceState, updateCharacter } from '../../lib/video.js';
import { useGeneration } from '../../state/generation.js';
import { useVideo } from '../../state/video.js';
import { useWorkspace } from '../../state/workspace.js';
import { LoopPlayer } from '../generation/previews/index.js';
import { Card, IconButton, NumberField, PropRow, SelectField, TextField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

/** A multi-line text field that commits on blur (like TextField). */
export function TextArea({ value, onChange, rows = 3, placeholder }: { value: string | undefined; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return (
    <textarea
      className="textfield vs-textarea"
      rows={rows}
      defaultValue={value ?? ''}
      key={value ?? ''}
      placeholder={placeholder}
      onBlur={(e) => {
        if (e.target.value !== (value ?? '')) onChange(e.target.value);
      }}
    />
  );
}

function CharacterCard({ c }: { c: Character }) {
  const projectId = useWorkspace((s) => s.projectId);
  const pieces = useVideo((s) => s.pieces);
  const dir = useVideo((s) => s.outputDir);
  const version = useVideo((s) => s.version);
  const catalog = useGeneration((s) => s.catalog);
  const s = useVideo.getState();
  const edit = (patch: Partial<Character>) => s.edit((d) => updateCharacter(d, c.id, patch));
  const img = characterImage(dir, c, pieces);
  const voice = c.voice ?? { engine: 'kokoro' as VoiceEngine };
  const sheet = pieceState(pieces, `character:${c.id}:sheet`);
  const test = pieceState(pieces, `character:${c.id}:voiceTest`);
  const voiceList = voice.engine === 'kokoro' ? catalog?.kokoroVoices : catalog?.chatterboxVoices;
  return (
    <Card
      title={c.name || c.id}
      icon="team"
      subtitle={c.id}
      defaultOpen={false}
      actions={<IconButton icon="trash" size={13} title="Remove character" onClick={() => s.edit((d) => ({ ...d, characters: d.characters.filter((x) => x.id !== c.id) }))} />}
    >
      <div className="vs-char">
        <div className="vs-char-img" title={img ? img : `Character sheet: ${STAGE_STATE_LABEL[sheet]}`}>
          {img && projectId ? <img src={rawUrl(projectId, img, version)} alt={c.name} /> : <Icon name="team" size={22} />}
          <span className={`vs-badge is-${sheet}`}>{sheet === 'none' ? 'no sheet' : STAGE_STATE_LABEL[sheet]}</span>
        </div>
        <div className="vs-char-fields">
          <PropRow label="Name">
            <TextField value={c.name} onChange={(name) => edit({ name })} />
          </PropRow>
          <PropRow label="Ref image" hint="A project image used as the identity reference instead of a generated sheet">
            <TextField value={c.refImages?.[0]} placeholder="(generated sheet)" onChange={(v) => edit({ refImages: v.trim() ? [v.trim()] : [] })} mono />
          </PropRow>
          <button type="button" className="ghost small" onClick={() => void s.render({ groups: ['characters'], keys: [`character:${c.id}:sheet`] })} title="Make the character sheet again (keyframe image model)">
            <Icon name="refresh" size={12} /> {sheet === 'none' ? 'Make sheet' : 'Regenerate sheet'}
          </button>
        </div>
      </div>
      <span className="muted vs-label">Look (used in every prompt the character is in)</span>
      <TextArea value={c.description} onChange={(description) => edit({ description })} placeholder="Face, hair, build, clothes, colors…" />
      <PropRow label="Voice model">
        <SelectField
          value={voice.engine}
          options={[
            { value: 'kokoro', label: 'Kokoro (very fast, natural)' },
            { value: 'chatterbox', label: 'Chatterbox (expressive, clones a reference WAV)' },
          ]}
          onChange={(engine) => edit({ voice: { ...voice, engine: engine as VoiceEngine } })}
        />
      </PropRow>
      <PropRow label="Voice">
        <TextField value={voice.voice} list={`vs-voices-${voice.engine}`} placeholder={voice.engine === 'kokoro' ? 'af_heart, am_michael…' : 'reference voice name'} onChange={(v) => edit({ voice: { ...voice, voice: v || undefined } as Character['voice'] })} />
        <datalist id={`vs-voices-${voice.engine}`}>
          {(voiceList ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </datalist>
      </PropRow>
      {voice.engine === 'chatterbox' && (
        <>
          <PropRow label="Clone WAV" hint="Project .wav of the voice to clone">
            <TextField value={voice.reference} placeholder="voices/ava.wav" mono onChange={(v) => edit({ voice: { ...voice, reference: v || undefined } as Character['voice'] })} />
          </PropRow>
          <PropRow label="Emotion" hint="Chatterbox exaggeration (0.25 calm - 2 wild). Empty: from each line's emotion.">
            <NumberField value={voice.exaggeration} min={0.25} max={2} step={0.05} onChange={(v) => edit({ voice: { ...voice, exaggeration: v } })} />
          </PropRow>
        </>
      )}
      <PropRow label="Delivery">
        <TextField value={voice.style} placeholder="gravelly, slow, warm…" onChange={(v) => edit({ voice: { ...voice, style: v || undefined } as Character['voice'] })} />
      </PropRow>
      <div className="vs-row">
        <button type="button" className="ghost small" onClick={() => void s.testVoice(c.id)} title="Speak a test line with this voice (local speech model)">
          <Icon name="audio" size={12} /> Test voice
        </button>
        <span className={`vs-badge is-${test}`}>{test === 'none' ? '' : STAGE_STATE_LABEL[test]}</span>
      </div>
      {(test === 'done' || test === 'review') && projectId && <LoopPlayer src={rawUrl(projectId, videoPaths.voiceTest(dir, c.id), version)} label={`${c.name} voice test`} />}
    </Card>
  );
}

export function StoryPanel() {
  const doc = useVideo((s) => s.doc);
  useEffect(() => {
    void useGeneration.getState().loadCatalog();
  }, []);
  if (!doc) return null;
  const s = useVideo.getState();
  const bible = doc.bible;
  const setBible = (patch: Partial<typeof bible>) => s.edit((d) => ({ ...d, bible: { ...d.bible, ...patch } }));
  return (
    <div className="inspector vs-story">
      <Card title="Story bible" icon="file">
        <span className="muted vs-label">Logline</span>
        <TextArea rows={2} value={bible.logline} onChange={(logline) => setBible({ logline })} placeholder="One sentence: who wants what, and what is in the way." />
        <span className="muted vs-label">Synopsis</span>
        <TextArea rows={5} value={bible.synopsis} onChange={(synopsis) => setBible({ synopsis })} placeholder="The whole story, beginning to end." />
        <PropRow label="Tone">
          <TextField value={bible.tone} onChange={(tone) => setBible({ tone })} placeholder="somber, hopeful…" />
        </PropRow>
        <span className="muted vs-label">Visual style (in every prompt)</span>
        <TextArea rows={2} value={bible.visualStyle} onChange={(visualStyle) => setBible({ visualStyle })} placeholder="cinematic photoreal, 35mm, warm dusk light…" />
        <span className="muted vs-label">World rules (one per line)</span>
        <TextArea rows={3} value={bible.worldRules.join('\n')} onChange={(v) => setBible({ worldRules: v.split('\n').map((x) => x.trim()).filter(Boolean) })} />
        <PropRow label="Avoid">
          <TextField value={bible.negative} onChange={(negative) => setBible({ negative: negative || undefined })} placeholder="added to every negative prompt" />
        </PropRow>
      </Card>
      <Card
        title="Characters"
        icon="team"
        subtitle={String(doc.characters.length)}
        actions={<IconButton icon="plus" size={13} title="Add character" onClick={() => s.edit((d) => addCharacter(d).doc)} />}
      >
        {doc.characters.length === 0 && <span className="muted">No characters yet. Add one, or let Prisma plan the story.</span>}
        {doc.characters.map((c) => (
          <CharacterCard key={c.id} c={c} />
        ))}
      </Card>
      <Card
        title="Locations"
        icon="mountain"
        subtitle={String(doc.locations.length)}
        defaultOpen={false}
        actions={<IconButton icon="plus" size={13} title="Add location" onClick={() => s.edit((d) => ({ ...d, locations: [...d.locations, { id: nextId('loc', d.locations.map((l) => l.id)), name: 'New location', description: '' }] }))} />}
      >
        {doc.locations.map((l) => (
          <div key={l.id} className="vs-loc">
            <div className="vs-row">
              <TextField value={l.name} onChange={(name) => s.edit((d) => ({ ...d, locations: d.locations.map((x) => (x.id === l.id ? { ...x, name } : x)) }))} />
              <code className="muted">{l.id}</code>
              <IconButton icon="trash" size={12} title="Remove location" onClick={() => s.edit((d) => ({ ...d, locations: d.locations.filter((x) => x.id !== l.id) }))} />
            </div>
            <TextArea rows={2} value={l.description} onChange={(description) => s.edit((d) => ({ ...d, locations: d.locations.map((x) => (x.id === l.id ? { ...x, description } : x)) }))} placeholder="What it looks like" />
          </div>
        ))}
      </Card>
    </div>
  );
}
